type AlarmDoItem = {
	notify?: string;
	msg?: string;
	notifyUI?: unknown;
	wait?: string | number;
	units?: string;
	scale?: string;
	wid?: number;
	do?: { blk?: AlarmDoItem[] };
	[key: string]: unknown;
};

type AlarmWithDo = { do?: AlarmDoItem[] };

const DELAY_WRAPPER_KEYS = new Set(['wait', 'units', 'scale', 'wid', 'do']);

const isNotifyAction = (item: AlarmDoItem | undefined): boolean =>
	typeof item?.notify === 'string' && item.notify !== 'event_log';

const isNotifyUI = (item: AlarmDoItem | undefined): boolean => item?.notifyUI != null;

const isDelayWrapped = (item: AlarmDoItem | undefined): boolean => Array.isArray(item?.do?.blk);

/**
 * Delay fields belong on the wait wrapper, not on the notify action itself.
 * Visiting the Delays step used to stamp `wait` onto old flat notify actions;
 * the backend then treats that action as a delay block and sends the default
 * "My scene triggered!" text instead of `msg`.
 */
const stripDelayFields = (action: AlarmDoItem): AlarmDoItem => {
	const rest: AlarmDoItem = {};
	for (const [key, value] of Object.entries(action)) {
		if (!DELAY_WRAPPER_KEYS.has(key)) {
			rest[key] = value;
		}
	}
	return rest;
};

/**
 * Finds the notify action the backend actually executes — nested inside
 * `do.blk` for current alarms, or at the top of `do` for legacy ones.
 */
export const findAlarmNotifyAction = (alarm: AlarmWithDo | undefined): AlarmDoItem | undefined => {
	for (const item of (alarm?.do ?? []) as AlarmDoItem[]) {
		if (isDelayWrapped(item)) {
			const nested = item.do?.blk?.find(isNotifyAction);
			if (nested) return nested;
		}
		if (isNotifyAction(item)) return item;
	}
	return undefined;
};

export const getAlarmNotificationMessage = (alarm: AlarmWithDo | undefined): string =>
	findAlarmNotifyAction(alarm)?.msg ?? '';

/**
 * Rewrites a legacy flat `do: [notify, notifyUI]` alarm into the current
 * `{ wait, do: { blk: [notify, notifyUI] } }` shape so delay + message stay
 * on the paths the backend and Delays step expect. No-op when already wrapped.
 */
export const normalizeAlarmDoBlock = <T extends AlarmWithDo>(alarm: T | undefined): T | undefined => {
	if (!alarm || !Array.isArray(alarm.do)) return alarm;

	const items = alarm.do as AlarmDoItem[];
	if (items.some(isDelayWrapped)) return alarm;

	const notify = items.find(isNotifyAction);
	if (!notify) return alarm;

	const notifyUI = items.find(isNotifyUI);
	const rest = items.filter((item) => !isNotifyAction(item) && !isNotifyUI(item));

	const wrapper: AlarmDoItem = {
		wait: notify.wait ?? '0',
		units: notify.units ?? 'sec',
		do: {
			blk: [stripDelayFields(notify), ...(notifyUI ? [notifyUI] : [])],
		},
	};

	if (notify.scale) wrapper.scale = notify.scale;
	if (typeof notify.wid === 'number') wrapper.wid = notify.wid;

	alarm.do = [wrapper, ...rest];
	return alarm;
};

export const setAlarmNotificationMessage = (alarm: AlarmWithDo | undefined, message: string): void => {
	if (!alarm) return;
	normalizeAlarmDoBlock(alarm);
	const action = findAlarmNotifyAction(alarm);
	if (action) {
		action.msg = message;
	}
};
